import "./App.css";
import UserProfile from "./components/UserProfile";
import TopBar from "./components/TopBar";
import SidePanel from "./components/SidePanel";
import { useState, useMemo, memo, Suspense, lazy } from "react";
import LoginPage from "./pages/LoginPage";
import { useEffect } from "react";
import { AnalyticsProvider } from "./utils/AnalyticsContext";
import { getApiUrl } from "./utils/apiConfig";
import SkeletonDashboard from "./components/SkeletonDashboard";
import CacheStatus from "./components/CacheStatus";
import userStore from "./utils/UserStore";
import { clearAllDateRangesFromStorage } from "./utils/DatePersistenceUtils";

// Lazy load all page components
const HomePage = lazy(() => import("./pages/HomePage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const ConversionRate = lazy(() => import("./pages/ConversionRate"));
const AnalyticDashboard = lazy(() => import("./pages/AnalyticDashboard"));
const QueryBuilderWrapper = lazy(() => import("./components/DynamicQueryBuilder").then((module) => ({ default: module.QueryBuilderWrapper })));
const CronJobPage = lazy(() => import("./pages/CronJobPage"));
const StripeDashboard = lazy(() => import("./pages/StripeDashboard"));
const ApiTestComponent = lazy(() => import("./components/ApiTestComponent"));
const RazorpayDashboard = lazy(() => import("./pages/RazorpayDashboard"));
const UpcomingRenewalsDashboard = lazy(() => import("./pages/UpcomingRenewalsDashboard"));
const RenewalCancellationDashboard = lazy(() => import("./pages/RenewalCancellationDashboard"));
const PaidUserDashboard = lazy(() => import("./pages/PaidUserDashboard"));
const FreeUserDashboard = lazy(() => import("./pages/FreeUserDashboard"));
const CohortAnalysisPage = lazy(() => import("./pages/CohortAnalysisPage"));
const UserDocAnalysis = lazy(() => import("./pages/UserDocAnalysis"));
const DailyReport = lazy(() => import("./pages/DailyReport"));
const GoogleAdsReport = lazy(() => import("./pages/GoogleAdsReport"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const Unsubscriber = lazy(() => import("./pages/Unsubscriber"));
const SignupToPayment = lazy(() => import("./pages/SignupToPayment"));
const ScoreBootAnalytics = lazy(() => import("./pages/ScoreBootAnalytics"));
const DocCreatedBeforeSubscription = lazy(() => import("./pages/DocCreatedBeforeSubscription"));
const EventTrackAnalytics = lazy(() => import("./pages/EventTrackAnalytics"));
const UpgradeNudgeEventTrack = lazy(() => import("./pages/UpgradeNudgeEventTrack"));
const PaymentEvents = lazy(() => import("./pages/PaymentEvents"));
const CronEmailTemplate = lazy(() => import("./pages/CronEmailTemplate"));
const StageTemplate = lazy(() => import("./pages/StageTemplate"));
const UserGrouping = lazy(() => import("./pages/UserGrouping"));
const CampaignList = lazy(() => import("./pages/CampaignList"));
const ScoreSheetCampaigns = lazy(() => import("./pages/ScoreSheetCampaigns"));
const BulkMailCampaign = lazy(() => import("./pages/BulkMailCampaign"));

// Loading component for Suspense fallback
const PageLoadingSpinner = ({ pageType }) => <SkeletonDashboard type={pageType} />;

function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activePage, setActivePage] = useState("analytics"); // Default page
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    useEffect(() => {
        checkToken();
        checkCronJob();
        clearAllDateRangesFromStorage();
        // Check if there's a saved page in localStorage
        const savedPage = localStorage.getItem("activePage");
        if (savedPage) {
            setActivePage(savedPage);
        }
    }, []);

    // Function to change the active page
    const navigateTo = (page) => {
        setActivePage(page);
        localStorage.setItem("activePage", page);
        // setIsSidebarOpen(false); // Close sidebar when navigating
    };

    // Function to toggle sidebar
    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const checkToken = async () => {
        const token = localStorage.getItem("token");
        if (token) {
            let response = await fetch(getApiUrl("/auth/protected"), {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            let data = await response.json();
            console.log(data);
            if (response.ok && data.user?.active) {
                // Store user data in singleton for global access
                userStore.setUser(data.user);
                setIsLoggedIn(true);
            } else {
                // Clear user data if login fails
                userStore.clearUser();
                setIsLoggedIn(false);
            }
        }
        setLoading(false);
    };

    const checkCronJob = async () => {
        let response = await fetch(getApiUrl("/users/cron/status"), {
            method: "GET",
        });

        let data = await response.json();
        console.log(data);
    };

    return (
        <>
            {!isLoggedIn && !loading && <LoginPage setIsLoggedIn={setIsLoggedIn} />}
            {isLoggedIn && !loading && (
                <div className="">
                    {/* Side Panel */}
                    <SidePanel navigateTo={navigateTo} activePage={activePage} isOpen={isSidebarOpen} />

                    {/* Main Content Area */}
                    <div className={`flex-1 flex flex-col transition-all duration-300 ease-in-out ${isSidebarOpen ? "ml-72" : "ml-0"}`}>
                        {/* Header - Using existing TopBar component */}
                        <TopBar navigateTo={navigateTo} activePage={activePage} isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

                        {/* Main Page Content */}
                        <div className="flex-1 overflow-scroll">
                            <Suspense fallback={<PageLoadingSpinner pageType="default" />}>
                                {/* Analytics Dashboard - default home page */}
                                {activePage === "analytics" && (
                                    <div className="page-container w-full h-full">
                                        <AnalyticsProvider>
                                            <AnalyticDashboard />
                                        </AnalyticsProvider>
                                    </div>
                                )}

                                {/* Event Track Analytics */}
                                {activePage === "event-track" && (
                                    <div className="page-container w-full h-full">
                                        <EventTrackAnalytics />
                                    </div>
                                )}

                                {/* Upgrade Nudge Event Track */}
                                {activePage === "upgrade-nudge-event-track" && (
                                    <div className="page-container w-full h-full">
                                        <UpgradeNudgeEventTrack />
                                    </div>
                                )}

                                {/* Payment Events */}
                                {activePage === "payment-events" && (
                                    <div className="page-container w-full h-full">
                                        <PaymentEvents />
                                    </div>
                                )}

                                {/* Users Page */}
                                {activePage === "users" && (
                                    <div className="page-container w-full h-full">
                                        <UsersPage navigateTo={navigateTo} />
                                    </div>
                                )}

                                {/* User Profile */}
                                {activePage === "userProfile" && (
                                    <div className="page-container w-full h-full">
                                        <UserProfile />
                                    </div>
                                )}

                                {/* Conversion Rate */}
                                {activePage === "conversionRate" && (
                                    <div className="page-container w-full h-full">
                                        <ConversionRate />
                                    </div>
                                )}

                                {/* Stripe Dashboard */}
                                {activePage === "stripe" && (
                                    <div className="page-container w-full h-full">
                                        <StripeDashboard />
                                    </div>
                                )}

                                {/* Razorpay Dashboard */}
                                {activePage === "razorpay" && (
                                    <div className="page-container w-full h-full">
                                        <RazorpayDashboard />
                                    </div>
                                )}

                                {/* Upcoming Renewals Dashboard */}
                                {activePage === "upcoming-renewals" && (
                                    <div className="page-container w-full h-full">
                                        <UpcomingRenewalsDashboard />
                                    </div>
                                )}

                                {/* Renewal Cancellation Dashboard */}
                                {activePage === "renewal-cancellation" && (
                                    <div className="page-container w-full h-full">
                                        <RenewalCancellationDashboard />
                                    </div>
                                )}

                                {/* Paid User Dashboard */}
                                {activePage === "paid-users" && (
                                    <div className="page-container w-full h-full">
                                        <PaidUserDashboard />
                                    </div>
                                )}

                                {/* Free User Dashboard */}
                                {activePage === "free-users" && (
                                    <div className="page-container w-full h-full">
                                        <FreeUserDashboard />
                                    </div>
                                )}

                                {/* Cohort Analysis */}
                                {activePage === "cohort-analysis" && (
                                    <div className="page-container w-full h-full">
                                        <CohortAnalysisPage />
                                    </div>
                                )}

                                {/* Doc Analysis */}
                                {activePage === "user-doc-analysis" && (
                                    <div className="page-container w-full h-full">
                                        <UserDocAnalysis />
                                    </div>
                                )}

                                {/* Daily Report */}
                                {activePage === "daily-report" && (
                                    <div className="page-container w-full h-full">
                                        <DailyReport />
                                    </div>
                                )}

                                {/* Google Ads Report */}
                                {activePage === "google-ads-report" && (
                                    <div className="page-container w-full h-full">
                                        <GoogleAdsReport />
                                    </div>
                                )}

                                {/* User Grouping */}
                                {activePage === "user-grouping" && (
                                    <div className="page-container w-full h-full">
                                        <UserGrouping />
                                    </div>
                                )}

                                {/* Campaign List */}
                                {activePage === "campaign-list" && (
                                    <div className="page-container w-full h-full">
                                        <CampaignList />
                                    </div>
                                )}

                                {/* Score Sheet Campaigns */}
                                {activePage === "score-sheet-campaigns" && (
                                    <div className="page-container w-full h-full">
                                        <ScoreSheetCampaigns />
                                    </div>
                                )}

                                {/* Signup to Payment */}
                                {activePage === "signup-to-payment" && (
                                    <div className="page-container w-full h-full">
                                        <SignupToPayment />
                                    </div>
                                )}

                                {/* Score Boot Analytics */}
                                {activePage === "score-boot-analytics" && (
                                    <div className="page-container w-full h-full">
                                        <ScoreBootAnalytics />
                                    </div>
                                )}

                                {/* Home Page */}
                                {activePage === "home" && (
                                    <div className="page-container w-full h-full">
                                        <HomePage />
                                    </div>
                                )}

                                {/* Only show admin-only pages if not a demo user */}
                                {!userStore.isDemoUser() && userStore.isLoggedIn() && (
                                    <>
                                        {/* API Test Component */}
                                        {activePage === "api-test" && (
                                            <div className="page-container w-full h-full">
                                                <ApiTestComponent />
                                            </div>
                                        )}

                                        {/* Cron Job Page */}
                                        {activePage === "cronJob" && (
                                            <div className="page-container w-full h-full">
                                                <CronJobPage />
                                            </div>
                                        )}

                                        {/* Query Builder */}
                                        {activePage === "querybuilder" && (
                                            <div className="page-container w-full h-full">
                                                <QueryBuilderWrapper navigateTo={navigateTo} />
                                            </div>
                                        )}

                                        {/* User Management */}
                                        {activePage === "user-management" && (
                                            <div className="page-container w-full h-full">
                                                <UserManagement />
                                            </div>
                                        )}

                                        {/* Unsubscriber */}
                                        {activePage === "unsubscriber" && (
                                            <div className="page-container w-full h-full">
                                                <Unsubscriber />
                                            </div>
                                        )}
                                        {/* Cron Email Template */}
                                        {activePage === "cron-email-template" && (
                                            <div className="page-container w-full h-full">
                                                <CronEmailTemplate />
                                            </div>
                                        )}
                                        {/* Bulk Mail Campaign */}
                                        {activePage === "bulk-mail-campaign" && (
                                            <div className="page-container w-full h-full">
                                                <BulkMailCampaign />
                                            </div>
                                        )}
                                        {/* Stage Template */}
                                        {activePage === "stage-template" && (
                                            <div className="page-container w-full h-full">
                                                <StageTemplate />
                                            </div>
                                        )}
                                        {/* Doc Created Before Subscription */}
                                        {activePage === "doc-created-before-subscription" && (
                                            <div className="page-container w-full h-full">
                                                <DocCreatedBeforeSubscription />
                                            </div>
                                        )}
                                    </>
                                )}
                            </Suspense>
                        </div>
                    </div>

                    {/* Debug component - only show in development */}
                    {process.env.NODE_ENV === "development" && <CacheStatus />}
                </div>
            )}
        </>
    );
}

export default App;
